import { Component, inject } from '@angular/core';
import { Store, StoreModule } from '@ngrx/store';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { ButtonComponent } from '../../../share/ava-button/ava-button.component';
import { FormSelectComponent } from '../../../share/form/controls/select';
import { tap } from 'rxjs/operators';
import { AppStore } from '@aaa/emember/store-types';
import {
  getIsLoadingRecostValidation,
  getJoinFormAccountValues,
  getJoinFormGiftAccountValues,
  getJoinFormMembershipAssociatesValues,
  getJoinFormPage,
  getJoinFormSelectedLevel,
  JoinActions,
  JoinPage,
} from '@aaa/emember/store-join';
import { DocumentRefService } from '../../../share/services/document-ref.service';
import { AvaIconComponent } from '../../../share/ava-icon/ava-icon.component';
import { getHideAddAssociate, getHideChangeMembershipLevel } from '@aaa/emember/store-price-offers';
import { AvaSkeletonComponent } from '../../../share/ava-skeleton';
import { ButtonLinkComponent } from '../../../share/ava-link/ava-link.component';
import { getMembershipLevels, MembershipLevel } from '../../../../store/price-offers/helpers/membership-levels';

@Component({
  selector: 'ava-join-sidebar-summary',
  template: `
    @let membershipAssociates = (membershipAssociatesValues$ | async)!;
    @let selectedMLevel = (selectedLevel$ | async)!;
    @let isLoading = (isLoading$ | async)!;
    @let hideAddAssociate = (hideAddAssociate$ | async)!;
    @let hideChangeMembershipLevel = (hideChangeMembershipLevel$ | async)!;
    @let page = (page$ | async)!;

    <div class="tw-font-mission tw-text-[22px] tw-font-normal">Summary</div>

    <div class="tw-flex tw-flex-col tw-gap-4">
      <div class="tw-flex tw-justify-between tw-gap-1" [formGroup]="formGroup">
        <div [formGroup]="formGroup" class="tw-flex tw-w-full tw-flex-col tw-gap-1">
          <button
            class="tw-flex tw-cursor-pointer tw-items-center tw-gap-4 tw-text-start tw-font-mission tw-text-sm tw-font-semibold hover:tw-text-blue-dark"
            (click)="handleSetActiveStepMembershipLevel(page)"
          >
            Membership Level
            <ava-icon iconType="link" />
          </button>
          <div class="tw-flex tw-items-center tw-justify-between !tw-gap-4">
            @if (!isLoading || selectedMLevel) {
              @if (membershipMode === 'view') {
                <span class="tw-text-sm tw-font-extralight">{{ selectedMLevel?.name }}</span>

                @if (!hideChangeMembershipLevel) {
                  <ava-link class="tw-px-0 tw-py-0 tw-text-sm" label="Edit" (clicked)="handleChangeMembership()" />
                }
              } @else {
                @if (membershipLevels) {
                  <ava-form-select
                    class="tw-min-w-24"
                    [options]="membershipLevels"
                    valueKey="membershipType"
                    displayLabel="name"
                    formControlName="membershipType"
                  />

                  <ava-link
                    class="tw-pb-0 tw-pr-0 tw-pt-0 tw-text-sm"
                    label="Save"
                    (clicked)="handleSaveMembership(membershipLevels)"
                  />
                }
              }
            } @else {
              <ava-skeleton size="small" [isLoading]="true" />
            }
          </div>
        </div>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-1">
        @if (page === 'join') {
          <button
            tabindex="0"
            class="tw-m-0 tw-flex tw-cursor-pointer tw-items-center tw-gap-4 tw-p-0 tw-text-start tw-font-mission tw-text-sm tw-font-semibold hover:tw-text-blue-dark"
            (click)="scrollToPrimaryMember()"
          >
            Primary Member
            <ava-icon iconType="link" />
          </button>
        }
        @if (page === 'gift') {
          <button
            tabindex="0"
            class="tw-m-0 tw-flex tw-cursor-pointer tw-items-center tw-gap-4 tw-p-0 tw-text-start tw-font-mission tw-text-sm tw-font-semibold hover:tw-text-blue-dark"
            (click)="scrollToGiftPurchaser()"
          >
            Gift Purchaser
            <ava-icon iconType="link" />
          </button>
        }
        @if (accountValues$ | async; as accountValues) {
          <div class="tw-min-h-4 tw-text-sm tw-font-extralight">
            {{ accountValues?.firstName }} {{ accountValues?.lastName }} {{ accountValues?.suffix }}
          </div>
        }
      </div>
      @if (page === 'gift') {
        <div class="tw-flex tw-flex-col tw-gap-1">
          <button
            tabindex="0"
            class="tw-flex tw-cursor-pointer tw-items-center tw-gap-4 tw-text-start tw-text-sm tw-font-semibold hover:tw-text-blue-dark"
            (click)="scrollToGiftRecipient()"
          >
            Gift Recipient
            <ava-icon iconType="link" />
          </button>
          @if (giftAccountValues$ | async; as accountValues) {
            <div class="tw-min-h-4 tw-text-sm tw-font-extralight">
              {{ accountValues?.firstName }} {{ accountValues?.lastName }} {{ accountValues?.suffix }}
            </div>
          }
        </div>
      }
      @if (!hideAddAssociate) {
        <div class="tw-flex tw-flex-col tw-gap-1">
          <button
            tabindex="0"
            class="tw-flex tw-cursor-pointer tw-items-center tw-gap-4 tw-text-start tw-font-mission tw-text-sm tw-font-semibold hover:tw-text-blue-dark"
            (click)="scrollToAssociateMembers(page)"
          >
            Associate Members
            <ava-icon iconType="link" />
          </button>
          @if (membershipAssociates.length) {
            <div class="tw-flex tw-min-h-4 tw-flex-col tw-gap-2 tw-text-sm tw-font-extralight">
              @for (membershipAssociate of membershipAssociates; track membershipAssociate; let i = $index) {
                @if (membershipAssociate?.firstName) {
                  <button
                    class="tw-text-start "
                    (click)="scrollToAssociateMembers(page) && scrollTo('membership-associate-' + i)"
                  >
                    {{ membershipAssociate?.firstName }} {{ membershipAssociate?.lastName }}
                    {{ membershipAssociate?.suffix }}
                  </button>
                }
              }
            </div>
          }
        </div>
      }
    </div>
    @if (!hideAddAssociate && membershipAssociates?.length === 0) {
      <ava-button
        (clicked)="handleAddAssociate()"
        (keydown.enter)="handleAddAssociate()"
        tabindex="0"
        size="small"
        type="text"
        leftIcon="plus-circle"
        iconTheme="twotone"
      >
        <span class="tw-cursor-pointer tw-font-mission tw-text-sm tw-font-semibold hover:tw-text-blue-dark">
          Add an Associate Member
        </span>
      </ava-button>
    }
  `,
  standalone: true,
  imports: [
    StoreModule,
    CommonModule,
    NzSelectModule,
    ButtonComponent,
    FormSelectComponent,
    ReactiveFormsModule,
    AvaIconComponent,
    AvaSkeletonComponent,
    ButtonLinkComponent,
  ],
  host: {
    class: 'tw-flex tw-flex-col !tw-gap-4',
  },
})
export class JoinSideBarSummaryComponent {
  store = inject(Store<AppStore>);
  fb = inject(FormBuilder);
  documentRef = inject(DocumentRefService);
  membershipMode = 'view';
  membershipLevels = getMembershipLevels();
  formGroup = this.getFormGroup();
  hideChangeMembershipLevel$ = this.store.select(getHideChangeMembershipLevel);
  hideAddAssociate$ = this.store.select(getHideAddAssociate);
  isLoading$ = this.store.select(getIsLoadingRecostValidation);
  membershipAssociatesValues$ = this.store.select(getJoinFormMembershipAssociatesValues);
  accountValues$ = this.store.select(getJoinFormAccountValues);
  page$ = this.store.select(getJoinFormPage);
  giftAccountValues$ = this.store.select(getJoinFormGiftAccountValues);
  selectedLevel$ = this.store
    .select(getJoinFormSelectedLevel)
    .pipe(
      tap((membershipCode) => this.formGroup.controls.membershipType.setValue(membershipCode?.membershipType || '')),
    );

  handleChangeMembership() {
    this.membershipMode = 'edit';
  }

  handleSaveMembership(levels: MembershipLevel[]) {
    const selectedLevel = levels.find((l) => l.membershipType === this.formGroup.value.membershipType);

    if (selectedLevel) {
      this.store.dispatch(JoinActions.changedMembershipLevel({ level: selectedLevel.level, rv: selectedLevel.rv }));
    }

    this.membershipMode = 'view';
  }

  handleAddAssociate() {
    this.store.dispatch(JoinActions.setFormActiveStep({ activeStep: 'memberInfo' }));
    this.store.dispatch(JoinActions.addAssociate({ quantity: 1 }));
    this.documentRef.scrollToId('membershipAssociates');
  }

  getFormGroup() {
    return this.fb.group({
      membershipType: new FormControl(''),
    });
  }

  handleSetActiveStepMembershipLevel(page: JoinPage) {
    if (page === 'join') {
      this.store.dispatch(JoinActions.setFormActiveStep({ activeStep: 'memberInfo' }));
      this.scrollTo('memberInfo');
    }

    if (page === 'gift') {
      this.store.dispatch(JoinActions.setFormActiveStep({ activeStep: 'giftReceiverInfo' }));
      this.scrollTo('giftReceiverInfo');
    }
  }

  scrollToGiftPurchaser() {
    this.store.dispatch(JoinActions.setFormActiveStep({ activeStep: 'memberInfo' }));
    this.scrollTo('memberInfo');
  }

  scrollToGiftRecipient() {
    this.store.dispatch(JoinActions.setFormActiveStep({ activeStep: 'giftReceiverInfo' }));
    this.scrollTo('giftReceiverInfo');
  }

  scrollToPrimaryMember() {
    this.store.dispatch(JoinActions.setFormActiveStep({ activeStep: 'memberInfo' }));
    this.scrollTo('memberInfo');
  }

  scrollToAssociateMembers(page: JoinPage) {
    if (page === 'join') {
      this.store.dispatch(JoinActions.setFormActiveStep({ activeStep: 'memberInfo' }));
      this.scrollTo('memberInfo');
    }

    if (page === 'gift') {
      this.store.dispatch(JoinActions.setFormActiveStep({ activeStep: 'giftReceiverInfo' }));
      this.scrollTo('giftReceiverInfo');
    }

    return true;
  }

  scrollTo(id: string) {
    this.documentRef.scrollToId(id);
  }
}
